import React, { useEffect, useLayoutEffect, useState } from 'react';
import Navigation from './navigation';
import Footer from './footer';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import AndroidBanner from './androidBanner';
import CompatibilityPopup from '../components/compatibilityPopup';
import { unlocalizedPageCheck } from '../utils/unlocalizedPageCheck';
import { NavToggleContext } from '../components/navToggleContext';
import { getShowPromoBanner, PromoBanner } from '../components/promo';
import LiteBanner from '../components/LiteBanner';

const favicon = `/images/${process.env.BRAND}/favicon.ico`;

const LocaleContext = React.createContext();

// Use the built-in Context API to make the "locale" available to every component in the tree
// This e.g. enables the LocalizedLink to function correctly
// As this component wraps every page (due to the wrapPageElement API) we can be sure to have
// the locale available everywhere!
const Layout = ({
    children,
    pageContext: { locale, unlocalizedPath, pageName, navText, meta, footerText, compatibilityText, androidBannerText },
}) => {
    if (typeof window !== 'undefined') {
        // eslint-disable-next-line global-require
        require('smooth-scroll')('a[href*="#"]', {
            topOnEmptyHash: false,
            header: '.navbar',
        });
    }
    useLayoutEffect(() => {
        if (!window.location.hash) {
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, 0);
        }
    }, [unlocalizedPath]);
    const { data } = useStaticQuery(query);
    const siteUrl = data.siteMetadata.siteUrl;
    let localeStr = locale.replace('_', '-');
    const ogUrl = locale === 'en' ? siteUrl + unlocalizedPath : `${siteUrl}/${localeStr}${unlocalizedPath}`;
    const description = stripHTML(meta.description);
    const defaultSocialImgFilename = process.env.BRAND === 'ut' ? 'og-utorrent.png' : 'bittorrent.png';
    const socialImg = meta.socialImgUrl
        ? `${siteUrl}/social/${meta.socialImgUrl}`
        : `${siteUrl}/social/${defaultSocialImgFilename}`;

    const isHomepage = pageName === 'index';
    const isBTWebFree = pageName === 'products_win_web-free';
    const isBTWebFreeMac = pageName === 'products_mac_web-free';
    const isBTWebPrem = pageName === 'products_win_web-premium';
    const isClassFree = pageName === 'products_win_classic-free';
    const isClassPrem = pageName === 'products_win_classic-premium';
    const isAndroFree = pageName === 'products_android_android-free';
    const isAndroPro = pageName === 'products_android_android-pro';
    const isAbout = pageName === 'company_about-us';

    const homepageStructuredData = [
        `{
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "BitTorrent",
            "legalName": "BitTorrent, Limited",
            "url": "https://www.bittorrent.com",
            "logo": "https://cdn-assets.bittorrent.com/knowledge-panel/BitTorrent_Logo_Black_Block.png",
            "foundingDate": "2001",
            "founder": { "@type": "Person", "name": "Bram Cohen" },
            "description": "BitTorrent is the creator of the world's leading peer-to-peer file sharing protocol, invented by Bram Cohen in 2001. BitTorrent software has been downloaded over 2 billion times across Windows, Mac, and Android.",
            "sameAs": [
                "https://www.facebook.com/bittorrent",
                "https://twitter.com/bittorrent",
                "https://www.instagram.com/bittorrent/",
                "https://www.youtube.com/channel/UCzYgPFi_9RqlZI6I7YQDh_Q",
                "https://en.wikipedia.org/wiki/BitTorrent_(software)"
            ],
            "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer support",
                "url": "https://www.bittorrent.com/help/"
            }
        }`,
        `{
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "BitTorrent",
            "url": "https://www.bittorrent.com",
            "potentialAction": {
                "@type": "SearchAction",
                "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://www.bittorrent.com/help/search/?query={search_term_string}"
                },
                "query-input": "required name=search_term_string"
            }
        }`,
        `{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What is BitTorrent?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "BitTorrent is the original peer-to-peer file sharing protocol, invented by Bram Cohen in 2001. BitTorrent software allows users to download and share large files efficiently using a decentralized network. It has been downloaded over 2 billion times worldwide."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Is BitTorrent free?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Yes, BitTorrent offers free versions of all its products including BitTorrent Web (Windows and Mac) and BitTorrent Classic (Windows). Pro versions with additional features like virus scanning and ad-free experience start at $19.95 per year."
                    }
                },
                {
                    "@type": "Question",
                    "name": "What platforms does BitTorrent support?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "BitTorrent is available on Windows (BitTorrent Web and BitTorrent Classic), Mac (BitTorrent Web), and Android. The software is available in 17 languages."
                    }
                }
            ]
        }`,
    ];

    const btwebfreeStructuredData = `{
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "BitTorrent Web",
        "url": "https://www.bittorrent.com/products/win/web-free",
        "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
        "downloadUrl": "https://www.bittorrent.com/downloads/complete",
        "operatingSystem": "WINDOWS",
        "applicationCategory": "https://schema.org/WebApplication",
        "offers": [
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD" },
            { "@type": "Offer", "name": "Pro", "price": "19.95", "priceCurrency": "USD", "billingIncrement": "P1Y" },
            { "@type": "Offer", "name": "Pro+VPN", "price": "69.95", "priceCurrency": "USD", "billingIncrement": "P1Y" }
        ]
    }`;

    const btwebfreemacStructuredData = `{
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "BitTorrent Web for Mac",
        "url": "https://www.bittorrent.com/products/mac/web-free",
        "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
        "downloadUrl": "https://www.bittorrent.com/downloads/complete",
        "operatingSystem": "MAC",
        "applicationCategory": "https://schema.org/WebApplication",
        "offers": [
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD" },
            { "@type": "Offer", "name": "Pro", "price": "19.95", "priceCurrency": "USD", "billingIncrement": "P1Y" }
        ]
    }`;

    const btwebpremStructuredData = [
        `{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "BitTorrent Web Pro",
            "url": "https://www.bittorrent.com/products/win/web-premium",
            "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
            "downloadUrl": "https://store.bittorrent.com/b_c_p_w",
            "operatingSystem": "WINDOWS",
            "applicationCategory": "https://schema.org/WebApplication",
            "offers": { "@type": "Offer", "price": "19.95", "priceCurrency": "USD" }
        }`,
        `{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "BitTorrent Web Pro+VPN",
            "url": "https://www.bittorrent.com/products/win/web-premium",
            "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
            "downloadUrl": "https://store.bittorrent.com/b_c_pv_w",
            "operatingSystem": "WINDOWS",
            "applicationCategory": "https://schema.org/WebApplication",
            "offers": { "@type": "Offer", "price": "69.95", "priceCurrency": "USD" }
        }`,
    ];

    const classicfreeStructuredData = `{
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "BitTorrent Classic",
        "url": "https://www.bittorrent.com/products/win/classic-free",
        "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
        "downloadUrl": "https://www.bittorrent.com/downloads/complete/classic",
        "operatingSystem": "WINDOWS",
        "applicationCategory": "https://schema.org/SoftwareApplication",
        "offers": [
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD" },
            { "@type": "Offer", "name": "Pro", "price": "19.95", "priceCurrency": "USD", "billingIncrement": "P1Y" },
            { "@type": "Offer", "name": "Pro+VPN", "price": "69.95", "priceCurrency": "USD", "billingIncrement": "P1Y" }
        ]
    }`;

    const classicpremStructuredData = [
        `{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "BitTorrent Classic Ad Free",
            "url": "https://www.bittorrent.com/products/win/classic-premium",
            "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
            "downloadUrl": "https://store.bittorrent.com/b_c_af_w",
            "operatingSystem": "WINDOWS",
            "applicationCategory": "https://schema.org/SoftwareApplication",
            "offers": { "@type": "Offer", "price": "6.95", "priceCurrency": "USD" }
        }`,
        `{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "BitTorrent Classic Pro",
            "url": "https://www.bittorrent.com/products/win/classic-premium",
            "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
            "downloadUrl": "https://store.bittorrent.com/b_c_p_w",
            "operatingSystem": "WINDOWS",
            "applicationCategory": "https://schema.org/SoftwareApplication",
            "offers": { "@type": "Offer", "price": "19.95", "priceCurrency": "USD" }
        }`,
        `{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "BitTorrent Classic Pro+VPN",
            "url": "https://www.bittorrent.com/products/win/classic-premium",
            "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
            "downloadUrl": "https://store.bittorrent.com/b_c_pv_w",
            "operatingSystem": "WINDOWS",
            "applicationCategory": "https://schema.org/SoftwareApplication",
            "offers": { "@type": "Offer", "price": "69.95", "priceCurrency": "USD" }
        }`,
    ];

    const androidfreeStructuredData = `{
        "@context": "https://schema.org",
        "@type": "MobileApplication",
        "name": "BitTorrent Android",
        "url": "https://www.bittorrent.com/products/android/android-free",
        "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
        "downloadUrl": "https://play.google.com/store/apps/details?id=com.bittorrent.client",
        "operatingSystem": "ANDROID",
        "applicationCategory": "https://schema.org/MobileApplication",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.5", "reviewCount": "951000", "bestRating": "5", "worstRating": "1" },
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
    }`;

    const androidproStructuredData = `{
        "@context": "https://schema.org",
        "@type": "MobileApplication",
        "name": "BitTorrent Android Pro",
        "url": "https://www.bittorrent.com/products/android/android-pro",
        "publisher": { "@type": "Organization", "name": "BitTorrent, Limited." },
        "downloadUrl": "https://play.google.com/store/apps/details?id=com.bittorrent.client.pro",
        "operatingSystem": "ANDROID",
        "applicationCategory": "https://schema.org/MobileApplication",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.4", "ratingCount": "8069" },
        "offers": { "@type": "Offer", "price": "2.99", "priceCurrency": "USD" }
    }`;

    const aboutStructuredData = [
        `{
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "BitTorrent",
            "legalName": "BitTorrent, Limited",
            "url": "https://www.bittorrent.com",
            "foundingDate": "2001",
            "founder": { "@type": "Person", "name": "Bram Cohen" },
            "description": "BitTorrent invented the peer-to-peer file transfer protocol in 2001. The protocol and software have been downloaded over 2 billion times worldwide. BitTorrent Limited has been part of the TRON ecosystem since 2018.",
            "sameAs": [
                "https://en.wikipedia.org/wiki/BitTorrent_(software)",
                "https://twitter.com/BitTorrent"
            ]
        }`,
        `{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What is BitTorrent?",
                    "acceptedAnswer": { "@type": "Answer", "text": "BitTorrent is a peer-to-peer (P2P) file transfer protocol created by Bram Cohen in 2001. Unlike traditional downloads from a single server, BitTorrent distributes files across a decentralized network of users, making large file transfers faster and more efficient. BitTorrent Limited develops free and paid torrent client software for Windows, Mac, and Android." }
                },
                {
                    "@type": "Question",
                    "name": "Is BitTorrent legal?",
                    "acceptedAnswer": { "@type": "Answer", "text": "Yes, BitTorrent is a legal file transfer technology. The BitTorrent protocol and software are completely legal to use. Like any file transfer tool, legality depends on what content is being shared — BitTorrent itself does not host, distribute, or endorse any specific content." }
                },
                {
                    "@type": "Question",
                    "name": "Is BitTorrent safe?",
                    "acceptedAnswer": { "@type": "Answer", "text": "BitTorrent software downloaded from the official website at bittorrent.com is safe. The Pro version includes integrated virus scanning powered by Safe Torrent Scanner to help protect against malicious files. Always download from the official site to ensure you have the authentic version." }
                },
                {
                    "@type": "Question",
                    "name": "How many people use BitTorrent?",
                    "acceptedAnswer": { "@type": "Answer", "text": "BitTorrent and its associated software have been downloaded over 2 billion times across Windows, Mac, and Android platforms, making it one of the most widely installed software applications in internet history." }
                },
                {
                    "@type": "Question",
                    "name": "Who created BitTorrent?",
                    "acceptedAnswer": { "@type": "Answer", "text": "BitTorrent was created by Bram Cohen, who invented the BitTorrent protocol in 2001 and founded BitTorrent, Inc. in 2004. The company was acquired by the TRON Foundation in 2018 and now operates as BitTorrent Limited." }
                },
                {
                    "@type": "Question",
                    "name": "What is the difference between BitTorrent Web and BitTorrent Classic?",
                    "acceptedAnswer": { "@type": "Answer", "text": "BitTorrent Web is a browser-based torrent client that lets you download and stream torrents without installing a standalone app. BitTorrent Classic is a full-featured desktop application for Windows that supports bulk downloads, advanced scheduling, and bandwidth management. Both are available in free and paid Pro versions." }
                }
            ]
        }`,
    ];

    let isUnlocalizedPage = unlocalizedPageCheck(unlocalizedPath);

    const hrefLangs = (
        <>
            <link rel="alternate" hreflang="de" href={`${siteUrl}/de${unlocalizedPath}`} />
            <link rel="alternate" hreflang="es" href={`${siteUrl}/es${unlocalizedPath}`} />
            <link rel="alternate" hreflang="fr" href={`${siteUrl}/fr${unlocalizedPath}`} />
            <link rel="alternate" hreflang="hi" href={`${siteUrl}/hi${unlocalizedPath}`} />
            <link rel="alternate" hreflang="it" href={`${siteUrl}/it${unlocalizedPath}`} />
            <link rel="alternate" hreflang="ja" href={`${siteUrl}/ja${unlocalizedPath}`} />
            <link rel="alternate" hreflang="ko" href={`${siteUrl}/ko${unlocalizedPath}`} />
            <link rel="alternate" hreflang="nl" href={`${siteUrl}/nl${unlocalizedPath}`} />
            <link rel="alternate" hreflang="pl" href={`${siteUrl}/pl${unlocalizedPath}`} />
            <link rel="alternate" hreflang="pt" href={`${siteUrl}/pt${unlocalizedPath}`} />
            <link rel="alternate" hreflang="pt-br" href={`${siteUrl}/pt-br${unlocalizedPath}`} />
            <link rel="alternate" hreflang="ru" href={`${siteUrl}/ru${unlocalizedPath}`} />
            <link rel="alternate" hreflang="tl" href={`${siteUrl}/tl${unlocalizedPath}`} />
            <link rel="alternate" hreflang="tr" href={`${siteUrl}/tr${unlocalizedPath}`} />
            <link rel="alternate" hreflang="zh-cn" href={`${siteUrl}/zh-cn${unlocalizedPath}`} />
            <link rel="alternate" hreflang="zh-tw" href={`${siteUrl}/zh-tw${unlocalizedPath}`} />
        </>
    );

    let helmet = (
        <Helmet>
            <html lang={locale} data-theme={process.env.BRAND} />
            <title>{meta.title}</title>
            <meta http-equiv="content-type" content="text/html; charset=UTF-8" />
            <meta name="viewport" content="width=device-width" />
            <meta name="description" content={description} />
            <meta name="keywords" content={meta.keywords} />
            <meta name="author" content="BitTorrent, Limited." />
            <meta property="og:url" content={ogUrl} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={meta.title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={socialImg} />
            <meta property="og:site_name" content="BitTorrent" />
            <meta name="twitter:site" content="@BitTorrent" />
            <meta name="twitter:image:src" content={socialImg} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta http-equiv="content-language" content={locale} />
            <link href={favicon} rel="shortcut icon" />
            <link rel="alternate" hreflang="en" href={`${siteUrl}${unlocalizedPath}`} />
            <link rel="alternate" hreflang="x-default" href={`${siteUrl}${unlocalizedPath}`} />

            {isHomepage &&
                homepageStructuredData.map((schema, i) => (
                    <script key={`bt-home-${i}`} type="application/ld+json">
                        {schema}
                    </script>
                ))}
            {isBTWebFree && <script type="application/ld+json">{btwebfreeStructuredData}</script>}
            {isBTWebFreeMac && <script type="application/ld+json">{btwebfreemacStructuredData}</script>}
            {isBTWebPrem &&
                btwebpremStructuredData.map((schema, i) => (
                    <script key={`bt-web-prem-${i}`} type="application/ld+json">
                        {schema}
                    </script>
                ))}
            {isClassFree && <script type="application/ld+json">{classicfreeStructuredData}</script>}
            {isClassPrem &&
                classicpremStructuredData.map((schema, i) => (
                    <script key={`bt-classic-prem-${i}`} type="application/ld+json">
                        {schema}
                    </script>
                ))}
            {isAndroFree && <script type="application/ld+json">{androidfreeStructuredData}</script>}
            {isAndroPro && <script type="application/ld+json">{androidproStructuredData}</script>}
            {isAbout &&
                aboutStructuredData.map((schema, i) => (
                    <script key={`bt-about-${i}`} type="application/ld+json">
                        {schema}
                    </script>
                ))}

            {!isUnlocalizedPage && hrefLangs.props.children}
            {/* declaring hrefLangs.props.children is a workaround to use fragment in helmet*/}

            {(meta.isCanonical === undefined || meta.isCanonical) && <link rel="canonical" href={ogUrl} />}
            <link href="/qne4zsu.css" rel="stylesheet" />
            <link href="/fonts/Inter/inter.css" rel="stylesheet" />
            {/* <script>
        {`
          window.simplexAsyncFunction = function () {
              Simplex.init({public_key: 'pk_live_00246184-710e-4751-b204-83b701670b62'});
              Simplex.subscribe('onlineFlowFinished', function(event){
                const result = event.payload.result;
                if(result) {
                  dataLayer.push({ 'event': 'simplex-checkout-' + (result === 'success' ? 'success' : 'failure') });
                  window.location.replace('/token/btt/');
                }
              })
          }
        `}
        </script> */}
            {/* <script src="https://cdn.simplex.com/sdk/v1/js/sdk.js" async></script> */}
            {/* Tracking for Simplex Widget */}
            {/* <script src="https://checkout.simplexcc.com/splx.js" async></script> */}
            {/* <script async src="https://www.googletagmanager.com/gtag/js?id=AW-10930619333"></script> */}
            {/*
          <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'AW-10930619333', { 'anonymize_ip': true });
          `}
          </script>
        */}
        </Helmet>
    );
    const [deviceInfo, setDeviceInfo] = useState({
        isAndroid: false,
        isIpad: false,
        isCrOS: false,
        isiOS: false,
    });
    const [banner, setBanner] = useState(null); // lite, android, promotion
    const [toggleNavPage, setToggleNavPage] = useState(null);

    useLayoutEffect(() => {
        // 仅在客户端执行设备检测
        setDeviceInfo({
            isAndroid: window.gatsbyIsAndroid || false,
            isIpad: window.gatsbyIsIpad || false,
            isCrOS: window.gatsbyIsCrOS || false,
            isiOS: window.gatsbyIsIos || false,
        });
    }, []);
    const { isAndroid, isIpad, isCrOS, isiOS } = deviceInfo;
    useEffect(() => {
        if (getShowPromoBanner()) {
            return setBanner('promotion');
        }
        if (!isHomepage) {
            return setBanner('lite');
        } else {
            return setBanner(null);
        }
    }, [isIpad, isiOS, isCrOS, isAndroid, isHomepage]);

    const getBanner = (type) => {
        if (type === 'lite') {
            return <LiteBanner navText={navText} locale={locale} />;
        } else if (type === 'android') {
            return <AndroidBanner locale={locale} text={androidBannerText} />;
        } else if (type === 'promotion') {
            return <PromoBanner />;
        }
    };

    let content = (
        <>
            <div className={'container-fluid px-0 mx-0'}>
                <CompatibilityPopup pageContext={{ text: compatibilityText, locale: locale }}>
                    <header className="global-header">
                        {banner !== null && getBanner(banner)}
                        <Navigation
                            pageContext={{
                                unlocalizedPath: unlocalizedPath,
                                navText: navText,
                            }}
                            toggleNavPage={toggleNavPage}
                        />
                    </header>

                    <main
                        className={`container-fluid shiroi px-0 ${banner !== null ? 'hasBanner' : ''}
                            ${banner === 'android' ? 'isAndroid' : ''}`}
                    >
                        <NavToggleContext.Provider value={(page) => setToggleNavPage(page)}>
                            {children}
                        </NavToggleContext.Provider>
                    </main>
                </CompatibilityPopup>

                <Footer pageContext={{ footerText: footerText }} />
            </div>
        </>
    );

    if (
        pageName === 'downloads_complete_classic' ||
        pageName === 'downloads_complete' ||
        pageName === 'downloads_complete_neo'
    ) {
        content = (
            <>
                <div className="container-fluid px-0 mx-0">
                    <CompatibilityPopup
                        pageContext={{
                            text: compatibilityText,
                            locale: locale,
                        }}
                    >
                        {banner !== null && getBanner(banner)}
                        <header className="global-header">
                            <Navigation
                                pageContext={{
                                    unlocalizedPath: unlocalizedPath,
                                    navText: navText,
                                }}
                                toggleNavPage={toggleNavPage}
                            />
                        </header>

                        <main className={`container-fluid shiroi px-0 ${banner !== null ? 'hasBanner' : ''}`}>
                            <NavToggleContext.Provider value={(page) => setToggleNavPage(page)}>
                                {children}
                            </NavToggleContext.Provider>
                        </main>
                    </CompatibilityPopup>
                </div>
            </>
        );
    }

    return (
        <LocaleContext.Provider value={{ locale }}>
            {helmet}
            {content}
        </LocaleContext.Provider>
    );
};

export { Layout, LocaleContext };

const query = graphql`
    query layout {
        data: site {
            siteMetadata {
                siteUrl
            }
        }
    }
`;

function stripHTML(string) {
    return string.replace(/<(.|\n)*?>/g, '');
}
